import * as React from 'react';
import { useMemo } from 'react';
import { useTranslations, getLanguage } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { CommunicationQuotas, RemindersSettingsData } from '../../../models';
import { BirthdayMessageSettingsData } from '../../../models/api-models';
import Typography from '../../../components/Typography';
import { CheckInputField, AutocompleteTextField, TimeField, TextInputField, PlaceholderTextField } from '../../../components/Forms/fields';
import { useGeneralSettings } from '../../../store/settings/hooks';
import { useDispatch } from 'react-redux';
import { alertError, alertGenericError, alertSuccess } from '../../../store/alerts/actions';
import { testReminderSmsAsync, testReminderWhatsAppAsync, testReminderEmailAsync } from '../../../ajax/requests';
import { getErrorMessage, isConflictError } from '../../../ajax/errors';
import {
    ChannelCard, ChannelHeader, ChannelInfo, ChannelContent, ChannelStatus,
    ChannelQuota, TestSection, TestActionButton, getMessageIcon, buildBirthdayTestData,
} from './remindersShared';
import { getBirthdayMessageDefaults } from './birthdayMessageTemplateDefaults';

// Lazy load HtmlEditor (259 KB with ReactQuill) - only loads when needed
const HtmlEditor = React.lazy(() => import('../../Campaigns/HtmlEditor').then(m => ({ default: m.HtmlEditor })));

export interface BirthdayMessageTabProps {
    values: RemindersSettingsData;
    onChange: (reminders: RemindersSettingsData) => void;
    communicationQuotas: CommunicationQuotas | null;
}

const BirthdayMessageTab: React.FC<BirthdayMessageTabProps> = ({ values, onChange, communicationQuotas }) => {
    const t = useTranslations();
    const dispatch = useDispatch();
    const generalSettings = useGeneralSettings();
    const [activeEditorId, setActiveEditorId] = React.useState<string | null>(null);
    const [emailTest, setEmailTest] = React.useState('');
    const [phoneNumberTest, setPhoneTest] = React.useState('');
    const [testing, setTesting] = React.useState(false);

    const templates = getBirthdayMessageDefaults(getLanguage());
    const clientTagOptions = useMemo(() => Object.keys(generalSettings.clientTags ?? {}), [generalSettings.clientTags]);

    const birthdayMessage: BirthdayMessageSettingsData = values.birthdayMessage ?? {};

    const update = (updates: Partial<BirthdayMessageSettingsData>) => {
        onChange({
            ...values,
            birthdayMessage: { ...birthdayMessage, ...updates },
        });
    };

    const handleEnableToggle = (enabled: boolean) => {
        if (enabled) {
            update({
                enabled: true,
                sendAtTime: birthdayMessage.sendAtTime ?? '09:00',
                smsEnabled: birthdayMessage.smsEnabled ?? false,
                smsText: birthdayMessage.smsText || templates.smsText,
                whatsAppEnabled: birthdayMessage.whatsAppEnabled ?? false,
                emailEnabled: birthdayMessage.emailEnabled ?? false,
                emailName: birthdayMessage.emailName || templates.emailName,
                emailSubject: birthdayMessage.emailSubject || templates.emailSubject,
                emailText: birthdayMessage.emailText || templates.emailText,
            });
        } else {
            update({ enabled: false });
        }
    };

    const handleChannelToggle = (channel: 'sms' | 'whatsapp' | 'email', checked: boolean) => {
        if (!checked) {
            if (channel === 'sms') update({ smsEnabled: false });
            else if (channel === 'whatsapp') update({ whatsAppEnabled: false });
            else update({ emailEnabled: false });
            return;
        }
        if (channel === 'sms') {
            update({ smsEnabled: true, smsText: birthdayMessage.smsText || templates.smsText });
        } else if (channel === 'whatsapp') {
            update({ whatsAppEnabled: true });
        } else {
            update({
                emailEnabled: true,
                emailName: birthdayMessage.emailName || templates.emailName,
                emailSubject: birthdayMessage.emailSubject || templates.emailSubject,
                emailText: birthdayMessage.emailText || templates.emailText,
            });
        }
    };

    const catchTestError = React.useCallback((error: unknown) => {
        const message = getErrorMessage(error);
        if (message) { dispatch(alertError(message)); return; }
        dispatch(isConflictError(error) ? alertError(t(TK.plafontExausted)) : alertGenericError());
    }, [dispatch, t]);

    const handleTestSms = React.useCallback(() => {
        if (!/^((\+\d{12})|(\d{9}))$/.test(phoneNumberTest)) {
            dispatch(alertError(t(TK.invalidPhoneNumber)));
            return;
        }
        setTesting(true);
        testReminderSmsAsync(buildBirthdayTestData(values, phoneNumberTest))
            .then(() => dispatch(alertSuccess(t(TK.sent))))
            .catch(catchTestError)
            .finally(() => setTesting(false));
    }, [phoneNumberTest, values, dispatch, catchTestError, t]);

    const handleTestWhatsApp = React.useCallback(() => {
        if (!/^((\+\d{12})|(\d{9}))$/.test(phoneNumberTest)) {
            dispatch(alertError(t(TK.invalidPhoneNumber)));
            return;
        }
        setTesting(true);
        testReminderWhatsAppAsync(buildBirthdayTestData(values, phoneNumberTest))
            .then(() => dispatch(alertSuccess(t(TK.sent))))
            .catch(catchTestError)
            .finally(() => setTesting(false));
    }, [phoneNumberTest, values, dispatch, catchTestError, t]);

    const handleTestEmail = React.useCallback(() => {
        if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/.test(emailTest)) {
            dispatch(alertError(t(TK.invalidEmail)));
            return;
        }
        setTesting(true);
        testReminderEmailAsync(buildBirthdayTestData(values, emailTest))
            .then(() => dispatch(alertSuccess(t(TK.sent))))
            .catch(catchTestError)
            .finally(() => setTesting(false));
    }, [emailTest, values, dispatch, catchTestError, t]);

    const buildUsageLabel = (used?: number, allowed?: number) =>
        used !== undefined && allowed !== undefined ? `${Math.min(used, allowed)}/${allowed}` : null;

    const smsUsageLabel = buildUsageLabel(communicationQuotas?.smsUsed, communicationQuotas?.smsAllowed);
    const whatsAppUsageLabel = buildUsageLabel(communicationQuotas?.whatsAppUsed, communicationQuotas?.whatsAppAllowed);

    const getChannelUsageLabel = (channel: 'sms' | 'whatsapp' | 'email') => {
        if (channel === 'sms') return smsUsageLabel;
        if (channel === 'whatsapp') return whatsAppUsageLabel;
        return null;
    };

    const renderEmailBody = (value: string, onBodyChange: (v: string) => void) => {
        if (activeEditorId === 'birthday-email') {
            return (
                <React.Suspense fallback={<div style={{ padding: 'var(--spacer-M)', textAlign: 'center' }}>Loading editor...</div>}>
                    <HtmlEditor value={value} onChange={onBodyChange} />
                </React.Suspense>
            );
        }
        return (
            <div
                style={{ border: 'var(--border-thin) solid var(--appLigthGrey)', borderRadius: 4, padding: 'var(--spacer-S)', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '0.875rem', minHeight: '60px', backgroundColor: 'var(--bg-light)' }}
                onClick={() => setActiveEditorId('birthday-email')}
                dangerouslySetInnerHTML={{ __html: value || '<em>Click to edit...</em>' }}
            />
        );
    };

    const channels = [
        { key: 'sms' as const, label: t(TK.sms), enabled: birthdayMessage.smsEnabled ?? false },
        { key: 'whatsapp' as const, label: 'WhatsApp', enabled: birthdayMessage.whatsAppEnabled ?? false },
        { key: 'email' as const, label: t(TK.email), enabled: birthdayMessage.emailEnabled ?? false },
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)' }}>
            <Typography size="large"><b>{t(TK.birthdayMessage)}</b></Typography>

            <CheckInputField
                label={t(TK.birthdayMessageEnabled)}
                helpText={t(TK.birthdayMessageEnabledHelp)}
                value={birthdayMessage.enabled ?? false}
                onChange={handleEnableToggle}
            />

            {(birthdayMessage.enabled ?? false) && (
                <>
                    <AutocompleteTextField
                        label={t(TK.birthdayMessageIncludeClientTags)}
                        helpText={t(TK.birthdayMessageIncludeClientTagsHelp)}
                        multiSelect
                        values={birthdayMessage.includeClientTags ?? []}
                        options={clientTagOptions}
                        placeholder={(birthdayMessage.includeClientTags?.length ?? 0) === 0 ? t(TK.birthdayMessageIncludeClientTagsPlaceholder) : undefined}
                        onValuesChanged={includeClientTags => update({ includeClientTags: includeClientTags.length ? includeClientTags : undefined })}
                    />

                    <AutocompleteTextField
                        label={t(TK.birthdayMessageExcludeClientTags)}
                        helpText={t(TK.birthdayMessageExcludeClientTagsHelp)}
                        multiSelect
                        values={birthdayMessage.excludeClientTags ?? []}
                        options={clientTagOptions}
                        onValuesChanged={excludeClientTags => update({ excludeClientTags: excludeClientTags.length ? excludeClientTags : undefined })}
                    />

                    <TimeField
                        label={t(TK.birthdayMessageSendAtTime)}
                        value={birthdayMessage.sendAtTime ?? '09:00'}
                        onChange={sendAtTime => update({ sendAtTime })}
                    />

                    <Typography size="large"><b>{t(TK.birthdayMessageChannels)}</b></Typography>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-XS)' }}>
                        {channels.map(({ key, label, enabled }) => {
                            const usageLabel = getChannelUsageLabel(key);
                            return (
                                <ChannelCard key={key} $isActive={enabled} $isSelected={enabled}>
                                    <ChannelHeader
                                        $isActive={enabled}
                                        $isSelected={enabled}
                                        onClick={() => { if (!enabled) return; }}
                                    >
                                        <CheckInputField
                                            label=""
                                            value={enabled}
                                            onChange={checked => handleChannelToggle(key, checked)}
                                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                        />
                                        <ChannelInfo>
                                            {getMessageIcon(key)}
                                            <Typography>
                                                <b>
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--spacer-XXS)', flexWrap: 'wrap' }}>
                                                        <span>{label}</span>
                                                        {usageLabel && <ChannelQuota>({usageLabel})</ChannelQuota>}
                                                    </span>
                                                </b>
                                            </Typography>
                                            <ChannelStatus $isActive={enabled}>
                                                {enabled ? t(TK.active) : t(TK.inactive)}
                                            </ChannelStatus>
                                        </ChannelInfo>
                                    </ChannelHeader>

                                    {enabled && (
                                        <ChannelContent>
                                            {key === 'sms' && (<>
                                                <PlaceholderTextField
                                                    id={`birthday-message-sms-text`}
                                                    label={t(TK.message)}
                                                    value={birthdayMessage.smsText ?? ''}
                                                    type='textarea'
                                                    maxLength={500}
                                                    onChange={smsText => update({ smsText })}
                                                />
                                                <Typography size="large"><b>{t(TK.testReminder)}</b></Typography>
                                                <TestSection>
                                                    <TextInputField
                                                        style={{ flexGrow: 1, minWidth: '200px' }}
                                                        value={phoneNumberTest}
                                                        onChange={setPhoneTest}
                                                        placeholder="+351910000000"
                                                    />
                                                    <TestActionButton disabled={testing} onClick={handleTestSms}>
                                                        {getMessageIcon('sms', { style: { marginRight: 'var(--spacer-XXS)' } })}
                                                        {t(TK.testSms)}
                                                    </TestActionButton>
                                                </TestSection>
                                            </>)}

                                            {key === 'whatsapp' && (<>
                                                <Typography size="smaller" style={{ color: 'var(--text-muted)' }}>
                                                    {t(TK.whatsAppTemplateMetaWarning)}
                                                </Typography>
                                                <PlaceholderTextField
                                                    id={`birthday-message-whatsapp-custom-farewell`}
                                                    label={t(TK.birthdayMessageWhatsAppCustomFarewell)}
                                                    helpText={t(TK.birthdayMessageWhatsAppCustomFarewellHelp)}
                                                    value={birthdayMessage.whatsAppCustomFarewell ?? ''}
                                                    maxLength={100}
                                                    onChange={whatsAppCustomFarewell => update({ whatsAppCustomFarewell: whatsAppCustomFarewell || undefined })}
                                                />
                                                <Typography size="large"><b>{t(TK.message)}</b></Typography>
                                                <div style={{
                                                    border: 'var(--border-thin) solid var(--appLigthGrey)',
                                                    borderRadius: 4,
                                                    padding: 'var(--spacer-S)',
                                                    color: 'var(--text-muted)',
                                                    fontSize: '0.875rem',
                                                    backgroundColor: 'var(--bg-light)',
                                                    whiteSpace: 'pre-wrap',
                                                    fontFamily: 'inherit',
                                                }}>
                                                    {templates.whatsAppText}
                                                </div>
                                            </>)}

                                            {key === 'email' && (<>
                                                <PlaceholderTextField
                                                    id={`birthday-message-email-name`}
                                                    label={t(TK.fromName)}
                                                    value={birthdayMessage.emailName ?? ''}
                                                    onChange={emailName => update({ emailName })}
                                                />
                                                <PlaceholderTextField
                                                    id={`birthday-message-email-subject`}
                                                    label={t(TK.subject)}
                                                    value={birthdayMessage.emailSubject ?? ''}
                                                    onChange={emailSubject => update({ emailSubject })}
                                                />
                                                <Typography size="large"><b>{t(TK.message)}</b></Typography>
                                                {renderEmailBody(
                                                    birthdayMessage.emailText ?? '',
                                                    emailText => update({ emailText }),
                                                )}
                                                <Typography size="large"><b>{t(TK.testReminder)}</b></Typography>
                                                <TestSection>
                                                    <TextInputField
                                                        style={{ flexGrow: 1, minWidth: '200px' }}
                                                        value={emailTest}
                                                        onChange={setEmailTest}
                                                        placeholder="xpto@domain.com"
                                                    />
                                                    <TestActionButton disabled={testing} onClick={handleTestEmail}>
                                                        {getMessageIcon('email', { style: { marginRight: 'var(--spacer-XXS)' } })}
                                                        {t(TK.testEmail)}
                                                    </TestActionButton>
                                                </TestSection>
                                            </>)}
                                        </ChannelContent>
                                    )}
                                </ChannelCard>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
};

export default BirthdayMessageTab;
